<script setup lang="ts">
import { computed } from 'vue'
import { sanitizeRichText } from '../richText'

// Shows a rich text field (UiRichTextEditor), or an old plain one with its line breaks. The HTML always
// goes through DOMPurify with the tags of richText.ts first: it came from people, and the server's
// sanitizer is not the only layer.
const props = defineProps<{ value: string | null | undefined }>()

const html = computed(() => sanitizeRichText(props.value))
</script>

<template>
  <!-- eslint-disable-next-line vue/no-v-html -- sanitized by DOMPurify (richText.ts) -->
  <div v-if="html" class="ui-richtext-view" v-html="html"></div>
</template>

<style scoped>
.ui-richtext-view {
  line-height: 1.5;
  overflow-wrap: anywhere;
}
.ui-richtext-view > :deep(:first-child) {
  margin-top: 0;
}
.ui-richtext-view > :deep(:last-child) {
  margin-bottom: 0;
}
.ui-richtext-view :deep(p) {
  margin: 0 0 0.6em;
}
.ui-richtext-view :deep(h3),
.ui-richtext-view :deep(h4) {
  margin: 0.8em 0 0.4em;
  font-size: var(--type-headline-size, 1.05em);
}
.ui-richtext-view :deep(ul),
.ui-richtext-view :deep(ol) {
  margin: 0 0 0.6em;
  padding-left: 1.4em;
}
.ui-richtext-view :deep(blockquote) {
  margin: 0 0 0.6em;
  padding-left: 10px;
  border-left: 3px solid var(--separator);
  color: var(--text-secondary);
}
.ui-richtext-view :deep(table) {
  width: 100%;
  margin: 0.5em 0 1em;
  border-collapse: separate;
  border-spacing: 0;
  border: 1px solid var(--separator);
  border-radius: var(--radius-md, 10px);
  overflow: hidden;
  font-size: var(--type-footnote-size, 0.92em);
  background: var(--surface-card);
}
.ui-richtext-view :deep(th),
.ui-richtext-view :deep(td) {
  padding: 7px 11px;
  text-align: left;
  vertical-align: top;
}
.ui-richtext-view :deep(td) {
  border-top: 1px solid var(--separator-soft, var(--separator));
}
.ui-richtext-view :deep(th) {
  background: var(--tint-accent-bg, var(--secondary-system-fill));
  color: var(--accent);
  font-size: 0.82em;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}
.ui-richtext-view :deep(tbody tr:first-child td) {
  border-top: 0;
}
.ui-richtext-view :deep(td:first-child) {
  color: var(--text-primary);
}
.ui-richtext-view :deep(code) {
  padding: 1px 5px;
  border-radius: 5px;
  background: var(--tint-accent-bg, var(--secondary-system-fill));
  color: var(--accent);
  font-size: 0.88em;
}
/* Colored notices: <blockquote data-callout="tip|warn|ok|err">, in the institution's own colors (theme tokens). */
.ui-richtext-view :deep(blockquote[data-callout]) {
  margin: 0.4em 0 1em;
  padding: 10px 14px;
  border: 0;
  border-left: 4px solid var(--accent);
  border-radius: var(--radius-md, 10px);
  background: var(--tint-accent-bg, var(--secondary-system-fill));
  color: var(--text-primary);
}
.ui-richtext-view :deep(blockquote[data-callout] p:last-child) {
  margin-bottom: 0;
}
.ui-richtext-view :deep(blockquote[data-callout] strong:first-child) {
  color: var(--accent);
}
.ui-richtext-view :deep(blockquote[data-callout='ok']) {
  border-left-color: var(--system-green);
  background: var(--tint-success-bg, var(--secondary-system-fill));
}
.ui-richtext-view :deep(blockquote[data-callout='ok'] strong:first-child) {
  color: var(--tint-success-fg, var(--system-green));
}
.ui-richtext-view :deep(blockquote[data-callout='warn']) {
  border-left-color: var(--system-orange);
  background: var(--tint-warning-bg, var(--secondary-system-fill));
}
.ui-richtext-view :deep(blockquote[data-callout='warn'] strong:first-child) {
  color: var(--tint-warning-fg, var(--system-orange));
}
.ui-richtext-view :deep(blockquote[data-callout='err']) {
  border-left-color: var(--danger);
  background: var(--tint-danger-bg, var(--secondary-system-fill));
}
.ui-richtext-view :deep(blockquote[data-callout='err'] strong:first-child) {
  color: var(--tint-danger-fg, var(--danger));
}
.ui-richtext-view :deep(a) {
  color: var(--accent);
}
</style>
